import { API_BASE_URL, ApiError, apiFetch, classifiedFetch, parseError, silentRefresh, type TokenStore } from './client.ts';
import type { EngagedByParty } from '../lib/roles.ts';
import { SIGNED_OUT_MESSAGE, errorMessage } from '../lib/error-labels.ts';
import { AGREEMENT_COPY_MIME_TYPES } from '../lib/agreement-origin.ts';
import type { PracticeInvitation } from './practices.ts';
import { createOneOffContractTemplateFromFile, sendContract, type SendContractResult } from './enquiries.ts';
import { uploadPracticeFile } from './files.ts';

/**
 * Cases API client (spec §8 lines 1279+). The v7 API is frozen; this mirrors
 * the live route shapes. The UI never grants access the API denies - a 403
 * (PRACTICE_ACCESS_DENIED / CASE_ACCESS_DENIED) or 404 (CASE_NOT_FOUND) from
 * the server renders as a denial state, never leaked data (inv-1, inv-2).
 *
 * No `storage_key` / filesystem path is ever present on these types - the API
 * never returns it (architecture fitness check, inv-14).
 */

/** Per-case triage rollups (V6 gaps 2-C / 16-B). */
export interface CaseRollup {
  activeTargetCount: number;
  lastSessionAt: number | null;
  sessionsThisWeek: number;
  openInvoiceCount: number;
  outstandingMinor: number;
  subTargetCount: number;
  subTargetActiveCount: number;
  subTargetMasteredCount: number;
}

export interface CaseSummary {
  id: string;
  practice_id: string;
  practice_name: string | null;
  case_reference: string | null;
  case_type: string;
  discipline_code: string;
  status: string;
  opened_at: number;
  client_first_name: string | null;
  client_last_name: string | null;
  /** Triage rollups; optional for safety. */
  rollups?: CaseRollup;
  /** True when the case has lost its lead practitioner and awaits reassignment (item 4.5). */
  reassignmentPending?: boolean;
  /** The actor's role on this case; drives the tutor card. */
  role_in_case?: string;
  /** Who engaged the actor on this case (`lib/roles.ts` mirrors the vocabulary).
   *  Splits the tutor desk — a client-engaged placement is a different commercial
   *  relationship from a clinic-supplied one. Absent on a projection that omits it. */
  engaged_by_party?: EngagedByParty;
  /** True when the actor is a tutor with no active tutor_access grant - the case is
   *  locked server-side and only a "pending approval" card is shown. */
  pendingTutorConfirmation?: boolean;
  /** Display name of the primary practitioner (case lead), null when vacant. */
  primary_practitioner_name: string | null;
}

export interface CaseListResponse {
  cases: CaseSummary[];
}

export interface SignedContractSummary {
  id: string;
  status: string;
  /** null/'electronic' = client e-signed; 'offline_attested' = quick-add attestation. */
  signingMethod: 'electronic' | 'offline_attested' | null;
  /** null until the outbox PDF worker has generated the signed artifact (GAP-7). */
  signedFileObjectId: string | null;
  signedFileName: string | null;
  awaitingSignature: { id: string; contactEmail: string | null; sentAt: number | null } | null;
  signerName: string | null;
  signedAt: number | null;
  // Item B - cancellation lifecycle. All null unless notice has been given.
  cancellationByParty?: 'practice' | 'client' | null;
  cancellationNoticeEndAt?: number | null;
  cancellationReason?: string | null;
  cancelledAt?: number | null;
}

/** Client (service recipient) demographics returned on the case detail (2-A). */
export interface CaseServiceRecipient {
  id: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  dateOfBirth: number | null;
}

/** Contact and service details from the originating enquiry, when present. */
export interface CaseEnquirySource {
  id: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactRelationship: string | null;
  location: string | null;
  settingName: string | null;
  fundingSource: string | null;
  referrerType: string | null;
  requestedHours: string | null;
  preferredSchedule: string | null;
}

/** The commercial arrangement (funding + referral) — the business-only tier of serviceDetails. */
export interface CaseBusinessDetails {
  fundingSource: string | null;
  referrerType: string | null;
}

export interface InvitedLead {
  invitationId: string;
  email: string | null;
  name: string | null;
  joined: boolean;
}

export interface CaseDetailResponse {
  case: CaseSummary & {
    invitedLead?: InvitedLead | null;
    firstLead?: boolean;
    /** The case LEAD. Served on the base case object — only funding_source/referrer_type are
     *  business-only (src/lib/case-business-view.ts). Optional so a payload without it can
     *  never be mistaken for "nobody is the lead". */
    primary_practitioner_user_id?: string;
    /** The billing/legal client record id, distinct from `serviceRecipient`. */
    client_record_id?: string | null;
    serviceRecipient?: CaseServiceRecipient | null;
    /** Free-text working note (V6 gap 2-D). */
    notes?: string | null;
    /** The OPERATIONAL tier — contact + logistics, on the base case for every member (snake_case). */
    contact_relationship?: string | null;
    contact_name?: string | null;
    contact_email?: string | null;
    contact_phone?: string | null;
    location?: string | null;
    setting_name?: string | null;
    requested_hours?: string | null;
    preferred_schedule?: string | null;
    /** The calling user's own role on this case - used for UI gating. */
    myCaseRole?: string | null;
    /**
     * The actor's server-resolved CASE capability set (plan §5.4). Read this instead
     * of re-deriving a permission from `myCaseRole`: the server owns the rule, and a
     * screen that re-derives it is how a button comes to be offered where the server
     * refuses, or hidden where it allows. Display only — every request is re-checked.
     */
    myCapabilities?: readonly string[];
    /** May I start (or back-date) a session? NOT derivable from `myCapabilities` —
     *  `encounter.start` also turns on four preconditions the grid never sees. Answered
     *  with the same call the write path makes, so the two cannot disagree. */
    canRecordSession?: boolean;
    /** The server's own session counts — `case-rollups.ts` excludes discarded. */
    rollups?: CaseRollup | null;
    recordSessionDenialCode?: string | null;
    canRecordAbcBetweenSessions?: boolean;
    betweenSessionAbcEnabled?: boolean;
    /** Sub-target progression delegation: the raw case override
     *  (null = inherit the practice default) and the resolved effective value. */
    subtargetProgressionDelegated?: boolean | null;
    subtargetProgressionEffective?: boolean;
  } & Record<string, unknown>;
  /** The signed agreement that originated this case, when present (GAP-7). */
  contract?: SignedContractSummary | null;
  /** Frozen intake snapshot from the originating enquiry (null for quick-added cases).
   *  Used to gate the contact/service sections ("this case came from an enquiry"); the
   *  editable values live in serviceDetails (case-owned). */
  enquirySource?: CaseEnquirySource | null;
  /** The case's LIVE commercial arrangement (funding + referral) — business-only, case-owned; null without business authority. */
  serviceDetails?: CaseBusinessDetails | null;
}

/**
 * Quick-add an existing/migrated client (#12, option C). Creates a fully-governed
 * case in one step (service recipient + case + members + consent + payer + an
 * offline-attested contract). `attestationReason` is mandatory (inv 6) - the
 * practitioner attests a signed service agreement is in place. No e-signature.
 */
export interface QuickAddCaseBody {
  serviceRecipientFirstName: string;
  serviceRecipientLastName: string;
  serviceRecipientDob?: number;
  caseType: 'child' | 'young_adult' | 'adult';
  consentModel: 'client_contact' | 'service_recipient' | 'shared' | 'best_interests';
  contactName?: string;
  contactEmail?: string;
  contactRelationship?: 'parent' | 'guardian' | 'service_recipient' | 'deputy' | 'other';
  /** Who takes clinical lead. Defaults to the creator. Required in practice when the
   *  creator holds no clinical credential — the server refuses an ineligible lead
   *  (TARGET_NOT_PRACTICE_MEMBER / TARGET_NOT_PRACTITIONER / CLINICAL_CAPABILITY_REQUIRED). */
  leadUserId?: string;
  acceptNoLead?: boolean;
  inviteLeadEmail?: string;
  attestationReason: string;
  signerName?: string;
  agreementFileObjectId?: string;
}

export async function quickAddCase(
  practiceId: string,
  body: QuickAddCaseBody,
  store: TokenStore,
  idempotencyKey?: string,
): Promise<{ caseId: string }> {
  return apiFetch<{ caseId: string }>(
    `/practices/${encodeURIComponent(practiceId)}/cases/quick-add`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      // A stable key across retries of the same submit → exactly one case.
      ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
    },
    store,
  );
}

export async function listCases(store: TokenStore): Promise<CaseListResponse> {
  return apiFetch<CaseListResponse>('/cases', {}, store);
}

export interface ArchivedCaseSummary {
  id: string;
  practice_id: string;
  practice_name: string;
  case_reference: string | null;
  client_first_name: string | null;
  client_last_name: string | null;
  opened_at: number;
  closed_at: number | null;
  archived_at: number | null;
  retention_until: number | null;
}

export async function listArchivedCases(store: TokenStore): Promise<{ cases: ArchivedCaseSummary[] }> {
  return apiFetch<{ cases: ArchivedCaseSummary[] }>('/cases/archived', {}, store);
}

/**
 * Case-scoped invitation (#2 follow-up) - an owner OR a practitioner who works
 * the case may invite someone (e.g. a tutor) to THIS case by email. Redeeming
 * adds them as a case member. Returns the one-time code (also emailed).
 */
export async function createCaseInvitation(
  caseId: string,
  body: {
    intendedRole: 'practitioner' | 'support_worker' | 'client_contact' | 'observer';
    intendedEmail?: string;
    /** The data-recording decision (0012); refused unless the inviter may make it. */
    dataEntryPregranted?: boolean;
  },
  store: TokenStore,
): Promise<{ invitationId: string; code: string }> {
  return apiFetch<{ invitationId: string; code: string }>(
    `/cases/${encodeURIComponent(caseId)}/invitations`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

export async function listCaseInvitations(
  caseId: string,
  store: TokenStore,
): Promise<PracticeInvitation[]> {
  const res = await apiFetch<{ invitations: PracticeInvitation[] }>(
    `/cases/${encodeURIComponent(caseId)}/invitations`,
    { method: 'GET' },
    store,
  );
  return res.invitations;
}

export async function getCase(caseId: string, store: TokenStore): Promise<CaseDetailResponse> {
  return apiFetch<CaseDetailResponse>(`/cases/${encodeURIComponent(caseId)}`, {}, store);
}

/** GET /cases/:id/client-contact-status — business read only. Whether the client has
 *  activated their account, and the latest email that tells them about it. */
export interface ClientContactStatus {
  accountActivated: boolean;
  invitable?: boolean;
  email: {
    status: string | null;
    failureReason: string | null;
    sentAt: number | null;
    templateType: string;
  } | null;
}

export async function getClientContactStatus(caseId: string, store: TokenStore): Promise<ClientContactStatus> {
  return apiFetch<ClientContactStatus>(
    `/cases/${encodeURIComponent(caseId)}/client-contact-status`, {}, store,
  );
}

export async function resendClientActivation(
  caseId: string,
  store: TokenStore,
): Promise<{ sent: boolean; sentTo: string }> {
  return apiFetch<{ sent: boolean; sentTo: string }>(
    `/cases/${encodeURIComponent(caseId)}/client-activation/resend`,
    { method: 'POST' },
    store,
  );
}

export interface CaseBusinessView {
  id: string;
  practice_id: string;
  service_recipient_id: string;
  client_record_id: string | null;
  client_user_id: string | null;
  case_reference: string | null;
  primary_practitioner_user_id: string;
  primary_practitioner_name: string | null;
  practice_name: string | null;
  discipline_code: string;
  case_type: string;
  consent_model: string;
  status: string;
  opened_at: number;
  closed_at: number | null;
  archived_at: number | null;
  archive_reason: string | null;
  retention_until: number | null;
  retention_basis: string | null;
  service_ended_at: number | null;
  service_ended_reason: string | null;
  contact_relationship: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  location: string | null;
  setting_name: string | null;
  funding_source: string | null;
  referrer_type: string | null;
  requested_hours: string | null;
  preferred_schedule: string | null;
  created_at: number;
  updated_at: number | null;
}

export interface CaseBusinessRead {
  case: CaseBusinessView;
  recipient: { firstName: string; lastName: string } | null;
  contract: SignedContractSummary | null;
  invitedLead: InvitedLead | null;
  firstLead: boolean;
}

export async function getCaseBusinessView(
  caseId: string,
  store: TokenStore,
): Promise<CaseBusinessRead> {
  return apiFetch<CaseBusinessRead>(
    `/cases/${encodeURIComponent(caseId)}/business`,
    {},
    store,
  );
}

// ── Contract cancellation (item B - cancel a signed agreement) ─────────────────

/**
 * Give notice to cancel a signed agreement. `noticeEndAt` is an epoch-ms date the
 * party chooses (null = immediate, no notice). HapMetrix does not validate it.
 */
export async function requestContractCancellation(
  contractId: string,
  body: { reason: string; noticeEndAt: number | null },
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/contracts/${encodeURIComponent(contractId)}/cancel`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

/** Withdraw a pending cancellation - the agreement returns to in-force. */
export async function withdrawContractCancellation(
  contractId: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/contracts/${encodeURIComponent(contractId)}/cancel/withdraw`,
    { method: 'POST' },
    store,
  );
}

/** Finalise a pending cancellation - active service ends; records are retained. */
export async function finaliseContractCancellation(
  contractId: string,
  store: TokenStore,
): Promise<{ ok: boolean; casePaused: boolean }> {
  return apiFetch<{ ok: boolean; casePaused: boolean }>(
    `/contracts/${encodeURIComponent(contractId)}/cancel/finalise`,
    { method: 'POST' },
    store,
  );
}

// ── Send an updated agreement (FB-0048) ─────────────────────────────────────────

/**
 * Draft an UPDATED agreement for a client already onboarded (either intake path).
 * `sendContract` (enquiries.ts, already generic on enquiry_id vs case_id) sends it.
 */
export async function createCaseContractUpdate(
  caseId: string,
  body: { templateId: string; contactEmail?: string; contactRelationship?: string },
  store: TokenStore,
): Promise<{ id: string; status: string }> {
  const res = await apiFetch<{ contract: { id: string; status: string } }>(
    `/cases/${encodeURIComponent(caseId)}/contracts`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
  return res.contract;
}

export async function sendCaseOneOffContract(
  caseId: string,
  practiceId: string,
  file: File,
  contactEmail: string | undefined,
  store: TokenStore,
): Promise<SendContractResult> {
  const tpl = await createOneOffContractTemplateFromFile(practiceId, file, store);
  const draft = await createCaseContractUpdate(caseId, { templateId: tpl.id, contactEmail }, store);
  return sendContract(draft.id, store);
}

export async function uploadAgreementCopy(practiceId: string, file: File, store: TokenStore): Promise<string> {
  if (!AGREEMENT_COPY_MIME_TYPES.includes(file.type)) {
    throw new ApiError(400, 'AGREEMENT_COPY_WRONG_TYPE', errorMessage('AGREEMENT_COPY_WRONG_TYPE', 400));
  }
  return (await uploadPracticeFile(practiceId, file, store)).fileObjectId;
}

export async function attachCaseAgreementCopy(
  caseId: string,
  practiceId: string,
  file: File,
  store: TokenStore,
): Promise<void> {
  const fileObjectId = await uploadAgreementCopy(practiceId, file, store);
  await apiFetch<{ ok: true }>(
    `/cases/${encodeURIComponent(caseId)}/agreement-copy`,
    { method: 'POST', body: JSON.stringify({ fileObjectId }) },
    store,
  );
}

/** Edit case fields (case_type / consent_model / discipline). PATCH /cases/:id */
export interface UpdateCaseBody {
  caseType?: string;
  consentModel?: string;
  disciplineCode?: string;
  /** Free-text working note (V6 gap 2-D). '' or null clears it. */
  notes?: string | null;
  // Live contact + service/logistics (V7.51) - case-owned, editable post-signing.
  // Omit = unchanged; null (or '' for the enum selects) = clear.
  contactRelationship?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  location?: string | null;
  settingName?: string | null;
  fundingSource?: string | null;
  referrerType?: string | null;
  requestedHours?: string | null;
  preferredSchedule?: string | null;
  /** Case override for sub-target progression delegation.
   *  Omit = unchanged; null = inherit the practice default; true/false = override. */
  subtargetProgressionDelegated?: boolean | null;
  betweenSessionAbcEnabled?: boolean;
}

export async function updateCase(
  caseId: string,
  body: UpdateCaseBody,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    store,
  );
}

/** Edit the client (service recipient) demographics - V6 gap 2-A. */
export interface UpdateServiceRecipientBody {
  firstName?: string;
  lastName?: string;
  preferredName?: string | null;
  dateOfBirth?: number | null;
}

export async function updateServiceRecipient(
  caseId: string,
  body: UpdateServiceRecipientBody,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/service-recipient`,
    { method: 'PATCH', body: JSON.stringify(body) },
    store,
  );
}

export interface CaseAuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  outcome: string;
  ts: number;
  /** Actor's role at the time (returned by the API; may be null). */
  actorRole?: string | null;
  /** Reason captured for sensitive mutations (inv 6); null otherwise. */
  reason?: string | null;
}

/** Case-scoped audit trail. GET /cases/:id/audit */
export async function getCaseAudit(caseId: string, store: TokenStore): Promise<CaseAuditEntry[]> {
  const res = await apiFetch<{ auditLog: CaseAuditEntry[] }>(
    `/cases/${encodeURIComponent(caseId)}/audit?limit=50`,
    {},
    store,
  );
  return res.auditLog;
}

// ── Members ─────────────────────────────────────────────────────────────────
// Shapes mirror the LIVE server (raw repo rows, snake_case). The rows carry
// user_id/role_in_case and consent_type_code; since human-testing #12 they also
// carry the joined display_name/email so the UI can render "Name - Role" instead
// of a raw nanoid.

export interface CaseMember {
  id: string;
  user_id: string;
  role_in_case: string;
  granted_at: number;
  revoked_at: number | null;
  /** Joined identity; null if the user row is gone. */
  display_name: string | null;
  email: string | null;
  /** 1 when the practice has confirmed this non-clinician has been trained to record data. */
  data_entry_permitted: number;
  /** Who brought this member; a client-engaged tutor is a different relationship. */
  engaged_by_party: EngagedByParty;
}

export async function listCaseMembers(
  caseId: string,
  store: TokenStore,
): Promise<{ members: CaseMember[]; tutorConsentRequired: boolean }> {
  return apiFetch<{ members: CaseMember[]; tutorConsentRequired: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/members`,
    {},
    store,
  );
}

// ── Consents ────────────────────────────────────────────────────────────────

export interface Consent {
  id: string;
  practiceId: string;
  caseId: string;
  consentTypeCode: string;
  /** The catalogue's own display name — do not restate the vocabulary in a screen. */
  consentTypeName: string | null;
  subjectUserId: string | null;
  /** For tutor_access consents: the support worker (tutor) this consent authorises. */
  targetUserId: string | null;
  targetName: string | null;
  grantedByUserId: string;
  grantedByName: string | null;
  grantorRole: string;
  status: string;
  grantedAt: number;
  revokedAt: number | null;
  revocationReason: string | null;
}

export async function listConsents(
  caseId: string,
  store: TokenStore,
): Promise<{ consents: Consent[]; grantableTypeCodes: string[] }> {
  return apiFetch<{ consents: Consent[]; grantableTypeCodes: string[] }>(
    `/cases/${encodeURIComponent(caseId)}/consents`,
    {},
    store,
  );
}

/**
 * Who may grant is src/lib/consent-grantor.ts, not a capability. Idempotent.
 * `evidenceEntityId` is required for behaviour_intervention_plan / functional_behaviour_assessment
 * — consent is per-document (a case may hold several concurrent FBAs/BSPs), not per-case.
 */
export async function grantConsent(
  caseId: string,
  body: { consentTypeCode: string; targetUserId?: string; evidenceEntityId?: string },
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/cases/${encodeURIComponent(caseId)}/consents`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

/**
 * Revoke a consent (invariant 6 — reason; invariant 10 — updated, never deleted). Revoking
 * the case's `practice_engagement` blocks clinical access (encounter.start).
 */
export async function revokeConsent(
  consentId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/consents/${encodeURIComponent(consentId)}/revoke`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

// ── Lifecycle (reason required, invariant 6) ─────────────────────────────────

export async function archiveCase(caseId: string, reason: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/archive`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

/** Discharge a case at end of treatment (reason required). Records are retained. */
export async function dischargeCase(caseId: string, reason: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/discharge`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

export async function restoreCase(caseId: string, reason: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/restore`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

export async function reopenCase(caseId: string, reason: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/reopen`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

export async function unarchiveCase(caseId: string, reason: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/unarchive`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

/** The record as one zip of what this reader may read; `withheld` names the kinds left out. */
export interface CaseRecordExport {
  fileObjectId: string;
  included: string[];
  withheld: string[];
}

export async function exportCaseRecord(caseId: string, store: TokenStore): Promise<CaseRecordExport> {
  return apiFetch<CaseRecordExport>(
    `/cases/${encodeURIComponent(caseId)}/record-export`,
    { method: 'POST', body: JSON.stringify({}) },
    store,
  );
}

/** Whom a handover removed from the case — the server resolves it, the actor cannot. */
export interface SeveredMember {
  userId: string;
  displayName: string | null;
}

export async function transferCase(
  caseId: string,
  reason: string,
  targetUserId: string,
  store: TokenStore,
): Promise<SeveredMember[]> {
  const res = await apiFetch<{ ok: boolean; severed: SeveredMember[] }>(
    `/cases/${encodeURIComponent(caseId)}/transfer`,
    { method: 'POST', body: JSON.stringify({ reason, targetUserId }) },
    store,
  );
  return res.severed;
}

/**
 * Roles a member can hold on a case (mirrors the server CASE_ROLES enum;
 * primary_practitioner is assigned at case creation/transfer, not added here).
 */
export type AddableCaseRole = 'practitioner' | 'support_worker' | 'client_contact' | 'observer';

export async function addCaseMember(
  caseId: string,
  userId: string,
  roleInCase: AddableCaseRole,
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/cases/${encodeURIComponent(caseId)}/members`,
    { method: 'POST', body: JSON.stringify({ userId, roleInCase }) },
    store,
  );
}

export async function removeCaseMember(
  caseId: string,
  userId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE', body: JSON.stringify({ reason }) },
    store,
  );
}

export async function setDataEntryPermission(
  caseId: string,
  userId: string,
  permitted: boolean,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/members/${encodeURIComponent(userId)}/data-entry-permission`,
    { method: 'PATCH', body: JSON.stringify({ permitted, reason }) },
    store,
  );
}

/** The practice says no to a still-PENDING ask, without switching recording on for anyone —
 *  the toggle above only ever moves it to the OPPOSITE of the current state, so it cannot
 *  express this while already unpermitted (adversarial review, prod round 2026-09-09: with
 *  no caller for this route, a refused ask stayed "Asked, the practice will confirm" forever). */
export async function declineDataEntryRequest(
  caseId: string,
  caseMemberId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ declined: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/data-entry-requests/decline`,
    { method: 'POST', body: JSON.stringify({ caseMemberId, reason }) },
    store,
  );
}

/**
 * Leave a case - the actor removes their own membership (V6 gap 3-D).
 * Any active member may leave; item 4.5 removed the last-practitioner block (the
 * case becomes "pending reassignment"). Reason required (invariant 6).
 */
export async function leaveCase(
  caseId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/leave`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

/**
 * Owner-only reassignment of a case's lead (item 4.5, Decision 4) - resolves a
 * "pending reassignment" case. POST /cases/:id/reassign. The owner need not be a
 * member of the case; the assignee must be a practitioner-type practice member.
 */
export async function reassignCase(
  caseId: string,
  targetUserId: string,
  reason: string,
  store: TokenStore,
): Promise<SeveredMember[]> {
  const res = await apiFetch<{ ok: boolean; severed: SeveredMember[] }>(
    `/cases/${encodeURIComponent(caseId)}/reassign`,
    { method: 'POST', body: JSON.stringify({ targetUserId, reason }) },
    store,
  );
  return res.severed;
}

/** A live case in the practice that has lost its lead practitioner (item 4.5). */
export interface PendingReassignmentCase {
  id: string;
  clientFirstName: string | null;
  clientLastName: string | null;
  invitedLead: InvitedLead | null;
  firstLead: boolean;
}

/**
 * Owner-only: live cases in the practice awaiting a new lead practitioner.
 * GET /practices/:id/pending-reassignments. Drives the dashboard "needs a lead"
 * task and the reassignment UI (the owner is usually not a member of these cases).
 */
export async function listPendingReassignments(
  practiceId: string,
  store: TokenStore,
): Promise<PendingReassignmentCase[]> {
  const res = await apiFetch<{ cases: PendingReassignmentCase[] }>(
    `/practices/${encodeURIComponent(practiceId)}/pending-reassignments`,
    { method: 'GET' },
    store,
  );
  return res.cases;
}

// ── Session edit requests ─────────────────────────────────────────────────────
// A locked (complete) session is corrected via an edit request: the conducting
// practitioner asks (POST /encounters/:id/edit-requests), and a primary
// practitioner approves (reverts the session to in_progress) or rejects it.

export type EditRequestStatus = 'pending' | 'approved' | 'rejected';

export interface SessionEditRequest {
  id: string;
  encounter_id: string;
  requested_by: string;
  reason: string;
  status: EditRequestStatus;
  reviewed_by: string | null;
  reviewed_at: number | null;
  created_at: number;
}

export async function listCaseEditRequests(
  caseId: string,
  store: TokenStore,
): Promise<SessionEditRequest[]> {
  const res = await apiFetch<{ editRequests: SessionEditRequest[] }>(
    `/cases/${encodeURIComponent(caseId)}/edit-requests`,
    {},
    store,
  );
  return res.editRequests;
}

export async function approveEditRequest(editRequestId: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/session-edit-requests/${encodeURIComponent(editRequestId)}/approve`,
    { method: 'POST', body: JSON.stringify({}) },
    store,
  );
}

export async function rejectEditRequest(
  editRequestId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/session-edit-requests/${encodeURIComponent(editRequestId)}/reject`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

/**
 * Download the case progress summary PDF (v7.117) - a fresh, ephemeral export,
 * generated on every call and never stored (no documents/file_objects row), so
 * it is fetched directly rather than via GET /files/:id (downloadFileObject in
 * ./files.ts). Same bearer-token + object-URL download pattern as that
 * function - the access token lives in memory, not a cookie, so a plain
 * `<a href>` would be unauthenticated.
 */
export async function downloadProgressSummary(caseId: string, store: TokenStore): Promise<void> {
  const fetchOnce = (token: string | null): Promise<Response> =>
    classifiedFetch(`${API_BASE_URL}/cases/${encodeURIComponent(caseId)}/progress-summary.pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    }, { timeoutMs: 300_000 });

  let res = await fetchOnce(store.getToken());
  if (res.status === 401) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      store.onRefresh(refreshed);
      res = await fetchOnce(refreshed.accessToken);
    } else {
      store.onAuthFailure();
    }
  }
  if (res.status === 401) {
    throw new ApiError(401, 'UNAUTHORIZED', SIGNED_OUT_MESSAGE);
  }
  if (!res.ok) {
    throw await parseError(res);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = `progress-summary-${caseId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * The BUSINESS VIEW case list (plan §8.1, D-3): every case the practice runs, for an owner
 * or admin who holds no case membership.
 *
 * WHY THIS EXISTS AS A SEPARATE CALL. `listCases` INNER JOINs `case_members` for the actor,
 * so an owner with no case role sees an EMPTY list — and `CaseListScreen` is the only link
 * into `/cases/:id` in the whole frontend. The consequence for §8.2: the clinical step-up's
 * door lives on the case's access-denied screen, which that owner could only reach by
 * already holding the case's opaque URL. The feature was unreachable for the one persona it
 * exists for, and the e2e specs could not see it because they `page.goto` straight to the id.
 *
 * The server decides who may call this (`case.view_business`, owner or admin) and audits
 * every read, success or denial — it discloses which clients a practice has.
 *
 * TYPED AS WHAT IT RETURNS, not as `CaseSummary`: it names the client `recipient_*`, and
 * `apiFetch` casts, so the wrong type was invisible and showed only as a fallback label.
 */
export interface BusinessCaseRow {
  id: string;
  status: string;
  case_type: string;
  discipline_code: string;
  opened_at: number;
  closed_at: number | null;
  archived_at: number | null;
  retention_until: number | null;
  service_ended_at: number | null;
  funding_source: string | null;
  case_reference: string | null;
  recipient_first_name: string;
  recipient_last_name: string;
  contact_name: string | null;
  contact_email: string | null;
  /** Front-office readers only; undefined means unanswered, not "no". */
  invitable?: boolean;
}

export async function listPracticeCases(
  practiceId: string,
  store: TokenStore,
): Promise<{ cases: BusinessCaseRow[] }> {
  return apiFetch<{ cases: BusinessCaseRow[] }>(
    `/practices/${encodeURIComponent(practiceId)}/cases`,
    {},
    store,
  );
}

/** GET/POST /cases/:id/client-email-correction — TF-0016 layer 4. Back office only. */
export interface ClientEmailCorrectionEligibility {
  correctable: boolean;
  /** Why not, when it cannot be corrected here. */
  refusal: string | null;
}

export async function getClientEmailCorrectionEligibility(
  caseId: string,
  store: TokenStore,
): Promise<ClientEmailCorrectionEligibility> {
  return apiFetch<ClientEmailCorrectionEligibility>(
    `/cases/${encodeURIComponent(caseId)}/client-email-correction`, {}, store,
  );
}

export async function correctClientEmail(
  caseId: string,
  body: { newEmail: string; reason: string },
  store: TokenStore,
): Promise<{ corrected: true }> {
  return apiFetch<{ corrected: true }>(
    `/cases/${encodeURIComponent(caseId)}/client-email-correction`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}
